import React from 'react';
import styled from 'styled-components';
import PackageBoxIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon';
import PalletIcon from '../../../../../generic/icons/IconLib/PalletIcon';
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon';
import ScaleIcon from '../../../../../generic/icons/IconLib/ScaleIcon';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { IOrderItem } from '../../../../../modules/API/Drivers/models/kavData';
import { useObjectImages } from '../../../../../generic/hooks/useObjectImages';
import { buildOrderImageRef } from '../helpers/imageRef';
import OrderImagesBlock from './OrderImagesBlock';
import { aggregateOrders } from '../helpers/aggregate';
import { DriversTag, IconSlot, IdText, MetricChip } from './DriversCardParts';

const NOTE_COLOR = '#EF4444';

export interface PalletsModalProps {
    order: IOrderItem | null;
    /** Инжектится AppModal. */
    closeFunc?: () => void;
}

const PalletsModal: React.FC<PalletsModalProps> = ({ order }) => {
    // Фото адресуются проблемой (приоритет) или теудой: без них запрос не уходит.
    const imageRef = order ? buildOrderImageRef(order) : null;
    const { images, comment, isLoading } = useObjectImages(imageRef);

    if (!order) return null;

    const { weight, pallets } = aggregateOrders([order]);
    const note = order.DistributNote?.trim();

    return (
        <Shell>
            {imageRef && (
                <OrderImagesBlock images={images} comment={comment} isLoading={isLoading} />
            )}

            <Summary>
                <MetricChip icon={<PalletIcon />} value={pallets.toFixed(2)} />
                <MetricChip icon={<ScaleIcon />} value={Math.round(weight)} unit='ק"ג' />
                <MetricChip icon={<PackageBoxIcon />} value={order.OrderID} />
            </Summary>

            {note && (
                <Summary>
                    <DriversTag variant='tinted' color={NOTE_COLOR}>
                        <NoteInner>
                            <PencilIcon size={14} />
                            {note}
                        </NoteInner>
                    </DriversTag>
                </Summary>
            )}

            {order.PalletsList.length === 0 ? (
                <EmptyText>אין משטחים לתעודה</EmptyText>
            ) : (
                <PalletList>
                    {order.PalletsList.map((pallet, index) => (
                        <CardRow
                            key={`${pallet.Pallet_Num}-${index}`}
                            $columns='auto minmax(0, 1fr) auto auto'
                            $mobileColumns='auto minmax(0, 1fr) auto auto'
                            $minHeight='auto'
                            $padding='0.5rem 0.625rem'
                            $mobilePadding='0.5rem 0.625rem'
                            $hover={false}
                            $isLast={index === order.PalletsList.length - 1}
                        >
                            <CardRow.Cell>
                                <IconSlot $muted>
                                    <PalletIcon size={14} />
                                </IconSlot>
                            </CardRow.Cell>
                            <CardRow.Cell>
                                <IdText>{pallet.Pallet_Num}</IdText>
                            </CardRow.Cell>
                            <CardRow.Cell $align='center'>
                                <MetricChip icon={<PackageBoxIcon />} value={pallet.Qty} />
                            </CardRow.Cell>
                            <CardRow.Cell $align='center'>
                                <MetricChip
                                    icon={<ScaleIcon />}
                                    value={Math.round(pallet.Gross)}
                                />
                            </CardRow.Cell>
                        </CardRow>
                    ))}
                </PalletList>
            )}
        </Shell>
    );
};

export default PalletsModal;

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    direction: rtl;
`;

const Summary = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem 0.5rem;
`;

const NoteInner = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: inherit;
`;

const PalletList = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.md};
    overflow: hidden;
    max-height: 22rem;
    overflow-y: auto;
    overscroll-behavior: contain;
`;

const EmptyText = styled.p`
    margin: 0;
    padding: 1rem 0;
    text-align: center;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink4};
`;
